import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useDelayedInitialization } from '../hooks/useDelayedInitialization';
import { useSqlRooms } from '../services/useSqlRooms';

const isDevelopment = process.env.NODE_ENV === 'development';

interface DuckDBContextType {
  query: <T = Record<string, unknown>[]>(sql: string, params?: Record<string, unknown>) => Promise<T>;
  getCached: (sql: string, params?: Record<string, unknown>) => any;
  ready: boolean;
  error: string | null;
  isInitializing: boolean;
  secondaryReady: boolean;
  dataVersion: number;
  reinitialize: () => Promise<void>;
  ensureMunicipalityBoundaries: () => Promise<void>;
  /** False on landing/about: WASM is not mounted; do not block navigation to dashboard. */
  routeHasDuckDB: boolean;
}

/** Shown when the route tree is not wrapped in LazyDuckDBProvider (e.g. `/`, `/about`). */
export const duckDbUnavailableContextValue: DuckDBContextType = {
  query: async () => {
    throw new Error('DuckDB is only available on dashboard routes (/kommun, /skola, /dashboard).');
  },
  getCached: () => undefined,
  ready: false,
  error: null,
  isInitializing: false,
  secondaryReady: false,
  dataVersion: 0,
  reinitialize: async () => {},
  ensureMunicipalityBoundaries: async () => {},
  routeHasDuckDB: false,
};

/** Default matches unavailable stub so `useDuckDB` is never `undefined` outside an explicit Provider. */
const DuckDBContext = createContext<DuckDBContextType>(duckDbUnavailableContextValue);

export const DuckDBUnavailableProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <DuckDBContext.Provider value={duckDbUnavailableContextValue}>{children}</DuckDBContext.Provider>
);

interface LazyDuckDBProviderProps {
  children: ReactNode;
  delayMs?: number;
}

export const LazyDuckDBProvider: React.FC<LazyDuckDBProviderProps> = ({
  children,
  delayMs = 500 // Increased from 0ms to 500ms to allow landing page to be interactive
}) => {
  const [isProviderActive, setIsProviderActive] = useState(false);
  const shouldInitialize = useDelayedInitialization(delayMs);

  // Only activate the provider after the delay
  useEffect(() => {
    if (shouldInitialize && !isProviderActive) {
      if (isDevelopment) console.log('🚀 Starting delayed DuckDB initialization after LCP');
      setIsProviderActive(true);
    }
  }, [shouldInitialize, isProviderActive]);

  // If provider is not active, return a mock context
  if (!isProviderActive) {
    const mockValue: DuckDBContextType = {
      query: async () => {
        throw new Error('DuckDB not yet initialized. Please wait for initialization to complete.');
      },
      getCached: () => undefined,
      ready: false,
      secondaryReady: false,
      error: null,
      isInitializing: true,
      dataVersion: 0,
      reinitialize: async () => {
        throw new Error('DuckDB not yet initialized. Cannot reinitialize.');
      },
      ensureMunicipalityBoundaries: async () => { },
      routeHasDuckDB: true,
    };

    return (
      <DuckDBContext.Provider value={mockValue}>
        {children}
      </DuckDBContext.Provider>
    );
  }

  // Provider is active, use the real DuckDB hook
  return <ActiveDuckDBProvider>{children}</ActiveDuckDBProvider>;
};

// Internal component that only renders when we're ready to initialize DuckDB
const ActiveDuckDBProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { query: executeQuery, ready, secondaryReady, error, getCached, dataVersion, reinitialize, ensureMunicipalityBoundaries } = useSqlRooms();

  // Create a generic query wrapper that maintains type safety
  const query = React.useCallback(<T = Record<string, unknown>[]>(sql: string, params?: Record<string, unknown>): Promise<T> => {
    return executeQuery(sql, params).then(result => {
      return result as unknown as T;
    });
  }, [executeQuery]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    query,
    getCached,
    ready,
    secondaryReady,
    error,
    isInitializing: !ready && !error,
    dataVersion,
    reinitialize,
    ensureMunicipalityBoundaries,
    routeHasDuckDB: true,
  }), [query, getCached, ready, secondaryReady, error, dataVersion, reinitialize, ensureMunicipalityBoundaries]);

  return (
    <DuckDBContext.Provider value={value}>
      {children}
    </DuckDBContext.Provider>
  );
};

export const useDuckDB = (): DuckDBContextType => useContext(DuckDBContext);

